import {
  BreadcrumbItem,
  ProductSEO,
  OrganizationSEO,
  FAQItem
} from './types';
import { DEFAULT_SEO } from './defaults';

/**
 * Generate Organization structured data (JSON-LD)
 */
export const generateOrganizationSchema = (orgData?: Partial<OrganizationSEO>): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: orgData?.name || DEFAULT_SEO.siteName,
    url: orgData?.url || DEFAULT_SEO.baseUrl,
    logo: orgData?.logo || `${DEFAULT_SEO.baseUrl}/logo.png`,
    description: orgData?.description || DEFAULT_SEO.defaultDescription,
    telephone: orgData?.telephone || DEFAULT_SEO.telephone,
    email: orgData?.email || DEFAULT_SEO.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: orgData?.address?.streetAddress || DEFAULT_SEO.address.streetAddress,
      addressLocality: orgData?.address?.addressLocality || DEFAULT_SEO.address.addressLocality,
      addressRegion: orgData?.address?.addressRegion || DEFAULT_SEO.address.addressRegion,
      postalCode: orgData?.address?.postalCode || DEFAULT_SEO.address.postalCode,
      addressCountry: orgData?.address?.addressCountry || DEFAULT_SEO.address.addressCountry
    },
    sameAs: orgData?.socialMedia || DEFAULT_SEO.socialMedia
  };

  return JSON.stringify(schema);
};

/**
 * Generate BreadcrumbList structured data (JSON-LD)
 */
export const generateBreadcrumbSchema = (items: BreadcrumbItem[]): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return JSON.stringify(schema);
};

/**
 * Generate Product structured data (JSON-LD)
 */
export const generateProductSchema = (product: ProductSEO): string => {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image,
    brand: {
      '@type': 'Brand',
      name: product.brand || DEFAULT_SEO.siteName
    }
  };

  if (product.sku) {
    schema.sku = product.sku;
  }

  if (product.price !== undefined) {
    schema.offers = {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: product.currency || 'USD',
      availability: `https://schema.org/${product.availability || 'InStock'}`,
      itemCondition: `https://schema.org/${product.condition || 'NewCondition'}`
    };
  }

  return JSON.stringify(schema);
};

/**
 * Generate FAQ structured data (JSON-LD)
 */
export const generateFAQSchema = (faqs: FAQItem[]): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return JSON.stringify(schema);
};

/**
 * Generate WebSite structured data with search action (JSON-LD)
 */
export const generateWebsiteSchema = (): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: DEFAULT_SEO.siteName,
    url: DEFAULT_SEO.baseUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${DEFAULT_SEO.baseUrl}/productList/search/{search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  return JSON.stringify(schema);
};

/**
 * Generate LocalBusiness structured data (JSON-LD)
 */
export const generateLocalBusinessSchema = (): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: DEFAULT_SEO.siteName,
    image: `${DEFAULT_SEO.baseUrl}/logo.png`,
    '@id': DEFAULT_SEO.baseUrl,
    url: DEFAULT_SEO.baseUrl,
    telephone: DEFAULT_SEO.telephone,
    email: DEFAULT_SEO.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: DEFAULT_SEO.address.streetAddress,
      addressLocality: DEFAULT_SEO.address.addressLocality,
      addressRegion: DEFAULT_SEO.address.addressRegion,
      postalCode: DEFAULT_SEO.address.postalCode,
      addressCountry: DEFAULT_SEO.address.addressCountry
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday'
      ],
      opens: '09:00',
      closes: '17:00'
    },
    sameAs: DEFAULT_SEO.socialMedia
  };

  return JSON.stringify(schema);
};
